import { GameType, PeriodRange } from '@statsbomb/parachute-types';
import { useTranslation } from 'react-i18next';
import { gameTypeOptions } from '@/consts/gameMetrics';
import { PERIOD_TRANSLATION_KEYS } from '@/consts/gameRange';
import { useGetGameCount } from '@/hooks/useGetGameCount';
import { OptionPromiseAtom } from '@/types/atom';
import { getTranslatedOptions } from '@/utils/translations';
import type { GamesBySeasonPromiseAtom } from '@/types/game';

export type GameSummaryInput = {
  selectedGamesAtom: OptionPromiseAtom;
  gamesGroupedBySeasonAtom: GamesBySeasonPromiseAtom;
  gameType: (typeof gameTypeOptions)[number];
  periodRange: PeriodRange;
  startDate: string;
  endDate: string;
};

/**
 * Builds a human-readable summary string describing the currently applied game filters.
 *
 * Filter values are passed in (read from `useFilters(scope)` by the caller) rather than
 * read from atoms, so the same hook serves any scope.
 *
 * Returns `"All matches"` when no filters are active. Otherwise, returns a comma-separated
 * string of the active filter labels, in the order: period range, date range, game type, game count.
 * Example: `"Last 5 games, From 2024-01-01, Home, 3 selected"`
 */
export const useGameSummary = ({
  selectedGamesAtom,
  gamesGroupedBySeasonAtom,
  gameType,
  periodRange,
  startDate,
  endDate,
}: GameSummaryInput): string => {
  const { t } = useTranslation('filters');

  const { allSelected, gameCount } = useGetGameCount(selectedGamesAtom, gamesGroupedBySeasonAtom);

  const startDateLabel = startDate ? `${t('dateFrom')} ${startDate}` : '';
  const endDateLabel = endDate ? `${t('dateTo')} ${endDate}` : '';
  const { key } = periodRange.translation;
  const period =
    key !== 'allGames' && PERIOD_TRANSLATION_KEYS.includes(key) ? t(key, { count: periodRange.value }) : null;
  const date = startDateLabel || endDateLabel ? `${t('dateRange')} ${startDateLabel} ${endDateLabel}`.trim() : null;

  const selectedGameTypeOption = getTranslatedOptions(gameTypeOptions).find(option => option.value === gameType.value);
  const gameTypeLabel =
    selectedGameTypeOption && selectedGameTypeOption.value !== GameType.HOME_AWAY ? selectedGameTypeOption.label : null;
  const count = !allSelected ? t('countSelected', { count: gameCount }) : null;

  return [period, date, gameTypeLabel, count].filter(Boolean).join(', ') || t('allGames');
};
